import {Platform, Pressable, View} from 'react-native'

import {atoms as a, useTheme} from '#/alf'
import {useInteractionState} from '#/components/hooks/useInteractionState'
import {AddFlashbook} from '#/components/icons/AddFlashbook'
import {Text} from '#/components/Typography'
import {BOOK_ASPECT} from './types'

/**
 * The way into the FlashBook work, as the first cell of the book grid.
 *
 * The same card as a capsule grid's `AddCapsuleCard`, deliberately: the two grids
 * sit side by side in the same tab strip, and a way in that looked different in each
 * would teach a reader that the two tabs are different *kinds* of thing rather than
 * two kinds of work. It is drawn in the grid's own proportions — a card with its own
 * caption under it — dashed and in the accent colour, because every other cell is a
 * finished piece of work and this one is a space where work goes. The mark is the
 * only thing that differs, and it names the work.
 *
 * Its proportions are the book card's, not the capsule card's: a book's cell is a
 * 4:5 cover with its facts under it, so a way in drawn at the capsule's taller 9:16
 * would stand a head above every book beside it and read as a different kind of
 * object. Everything else about the two cards is identical, including the caption
 * under them, because that is what says they are the same *kind of thing* — a slot
 * where work goes — in two grids of different work.
 *
 * Like the capsule's, what it opens is the caller's: a classroom's grid opens the
 * picker of books this account has already published, and a creator's own profile
 * opens the studio, because a profile has nothing to share with itself.
 */
export function AddFlashbookCard({
  onPress,
  testID,
  label,
  caption,
  aspectRatio = BOOK_ASPECT,
}: {
  onPress: () => void
  /** The recipe that drives this grid names its own cells. */
  testID: string
  /** What the card says on itself: the act, in the caller's words. */
  label: string
  /** The line under it, in the place a book keeps its title and author. */
  caption: string
  /** The grid's proportions: the book card's, unless the caller says otherwise. */
  aspectRatio?: number
}) {
  const t = useTheme()
  const {
    state: pressed,
    onIn: onPressIn,
    onOut: onPressOut,
  } = useInteractionState()

  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityHint={caption}
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      style={[a.flex_col, Platform.OS === 'web' ? {cursor: 'pointer'} : null]}>
      <View
        style={[
          a.justify_center,
          a.align_center,
          a.rounded_md,
          a.gap_sm,
          a.p_md,
          {
            aspectRatio,
            borderWidth: 1.5,
            borderStyle: 'dashed',
            borderColor: t.palette.primary_500,
            /** See `AddCapsuleCard`: filled faintly, deepened when pressed. */
            backgroundColor: pressed
              ? t.atoms.bg_contrast_50.backgroundColor
              : t.atoms.bg_contrast_25.backgroundColor,
          },
          Platform.OS === 'web' && a.w_full,
        ]}>
        {/*
          The mark for the act this card invites: a book with a plus in it, drawn in
          the accent colour, the one thing on this card that is not a whisper.
        */}
        <AddFlashbook size="4xl" fill={t.palette.primary_500} />

        <Text
          style={[
            a.text_sm,
            a.font_semi_bold,
            a.text_center,
            {color: t.palette.primary_500},
          ]}>
          {label}
        </Text>
      </View>

      {/*
        The handful of words a card keeps under it, in the place a book keeps its
        title and author.
      */}
      <View style={[a.pr_xs, {paddingTop: 6, gap: 4}]}>
        <Text
          style={[a.text_sm, a.leading_tight, t.atoms.text_contrast_medium]}
          numberOfLines={2}>
          {caption}
        </Text>
      </View>
    </Pressable>
  )
}
